import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupCountBy, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { ProductionOrderRegisterRow, ProductionYieldRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

export default function ProductionPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: productionOrders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['reports', 'production-order-register'],
    queryFn: async () => (await apiClient.get<ProductionOrderRegisterRow[]>('/reports/production-order-register')).data,
  });
  const { data: productionYield = [], isLoading: loadingYield } = useQuery({
    queryKey: ['reports', 'production-yield'],
    queryFn: async () => (await apiClient.get<ProductionYieldRow[]>('/reports/production-yield')).data,
  });

  const totalOrders = useMemo(() => new Set(productionOrders.map((r) => r.productionOrderId)).size, [productionOrders]);
  const producedQty = useMemo(() => productionOrders.reduce((sum, r) => sum + r.producedQty, 0), [productionOrders]);
  const avgYield = useMemo(() => average(productionYield.map((r) => r.yieldPercent).filter((v): v is number => v != null)), [productionYield]);
  const rejectedQty = useMemo(() => productionYield.reduce((sum, r) => sum + r.rejectedQty, 0), [productionYield]);

  const plannedVsProduced = useMemo(
    () => groupSumByMonth(productionOrders, (r) => r.plannedDate, [
      { key: 'planned', value: (r) => r.plannedQty },
      { key: 'produced', value: (r) => r.producedQty },
    ]),
    [productionOrders],
  );
  const statusDistribution = useMemo(() => groupCountBy(productionOrders, (r) => r.status), [productionOrders]);
  const topYieldOrders = useMemo(() => topN(productionYield, (r) => r.producedQty, 10).reverse(), [productionYield]);

  const yieldColumns: DataTableColumn<ReturnType<typeof withId<ProductionYieldRow>>[number]>[] = [
    { field: 'orderNumber', headerName: tr('columns.number') },
    { field: 'materialCode', headerName: tr('columns.materialCode') },
    { field: 'plannedQty', headerName: tr('columns.plannedQty'), align: 'right' },
    { field: 'producedQty', headerName: tr('columns.producedQty'), align: 'right' },
    { field: 'yieldPercent', headerName: t('production.yieldPercent'), align: 'right', render: (r) => (r.yieldPercent != null ? `${r.yieldPercent.toFixed(1)}%` : '—') },
    { field: 'rejectionPercent', headerName: t('production.rejectionPercent'), align: 'right', render: (r) => (r.rejectionPercent != null ? `${r.rejectionPercent.toFixed(1)}%` : '—') },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('production.totalOrders')} value={totalOrders} icon={<PrecisionManufacturingIcon />} accentColor={colors.production} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('production.producedQty')} value={producedQty.toLocaleString()} icon={<Inventory2Icon />} accentColor={colors.production} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('production.avgYield')} value={avgYield > 0 ? `${avgYield.toFixed(1)}%` : '—'} icon={<TrendingUpIcon />} accentColor={colors.production} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('production.rejectedQty')} value={rejectedQty.toLocaleString()} icon={<CancelOutlinedIcon />} accentColor={colors.adjustments} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('production.plannedVsProducedTitle')} subtitle={t('production.plannedVsProducedSubtitle')} loading={loadingOrders} empty={!loadingOrders && plannedVsProduced.length === 0}>
            <DashboardBarChart
              categories={plannedVsProduced.map((m) => m.month)}
              series={[
                { label: tr('columns.plannedQty'), data: plannedVsProduced.map((m) => m.values.planned), color: colors.procurement },
                { label: tr('columns.producedQty'), data: plannedVsProduced.map((m) => m.values.produced), color: colors.production },
              ]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('production.statusTitle')} loading={loadingOrders} empty={!loadingOrders && statusDistribution.length === 0}>
            <DashboardPieChart data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('production.yieldByOrderTitle')} loading={loadingYield} empty={!loadingYield && topYieldOrders.length === 0} height={300}>
        <DashboardBarChart
          layout="horizontal"
          categories={topYieldOrders.map((r) => r.orderNumber)}
          series={[{ label: t('production.yieldPercent'), data: topYieldOrders.map((r) => r.yieldPercent ?? 0), color: colors.production }]}
          valueFormatter={(v) => `${(v ?? 0).toFixed(1)}%`}
          height={300}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('production.yieldTableTitle')}
        </Typography>
        <DataTable columns={yieldColumns} rows={withId(productionYield, (r) => r.productionOrderId)} loading={loadingYield} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
